/**
 * shadcn-style Input — 32px height, 13px text, warm focus ring.
 *
 * ═══ ⚠️ 44px ПО КАСАНИЮ — ПРАВКА Ф4 · В6-б, И ОНА В ПРИМИТИВЕ ═══════════
 *
 * Базовые `h-8` — 32px, при норме касания 44 (WCAG 2.5.5). Кнопки норму уже
 * держат (`button.tsx`), а поля ввода не держали НИ ОДНО: на новом экране это
 * шесть полей разом — поиск, название, описание, оценка и обе даты, — и
 * основной сценарий волны прораб открывает с планшета.
 *
 * ⚠️ ПРАВИТСЯ В ПРИМИТИВЕ, А НЕ У ПОТРЕБИТЕЛЯ, по тому же доводу, что у
 * `Combobox` и `Button`: точечный класс на новом экране оставил бы прежние
 * формы 32-пиксельными и завёл бы ВТОРУЮ норму касания в одном продукте.
 * Вариант с `@media(pointer:coarse)` добавляет высоту только там, где палец, и
 * вид на мыши не трогает — ни один существующий экран не «разъезжается».
 */
import * as React from 'react';
import { cn } from '@/lib/utils';

export type InputProps = React.InputHTMLAttributes<HTMLInputElement>;

const Input = React.forwardRef<HTMLInputElement, InputProps>(
  ({ className, type = 'text', ...props }, ref) => (
    <input
      ref={ref}
      type={type}
      className={cn(
        'flex h-8 w-full rounded-sm border border-input bg-surface px-3 text-[13px] text-ink shadow-1 transition-colors duration-short',
        '[@media(pointer:coarse)]:h-11',
        'placeholder:text-muted file:border-0 file:bg-transparent file:text-sm file:font-medium',
        'focus-visible:outline-none focus-visible:border-accent focus-visible:ring-[3px] focus-visible:ring-accent-wash',
        'disabled:cursor-not-allowed disabled:opacity-50',
        className,
      )}
      {...props}
    />
  ),
);
Input.displayName = 'Input';

/**
 * Многострочное поле — Блок 2 · Ф4 · В6-б.
 *
 * ⚠️ ЗАВЕДЕНО ПОТОМУ, ЧТО ОДНОСТРОЧНЫЙ ВВОД ПРИ ЛИМИТЕ 2000 СИМВОЛОВ — ЛОЖЬ
 * ИНТЕРФЕЙСА, а не неудобство. `DescriptionSchema` разрешает 2000 символов;
 * `<input>` показывает из них строку, и человек не видит, что набрал.
 *
 * ⚠️ АЛЬТЕРНАТИВА «СНИЗИТЬ ЛИМИТ ДО 300» ОТВЕРГНУТА: контракт — общий, его
 * держит сервер и бот, и сужать его ради отсутствия примитива значило бы
 * подгонять предмет под инструмент. Примитив стоит двадцати строк.
 *
 * Оформление повторяет `Input` дословно, кроме высоты: одна форма ввода на
 * продукт, а не вторая рядом с первой.
 */
export type TextareaProps = React.TextareaHTMLAttributes<HTMLTextAreaElement>;

const Textarea = React.forwardRef<HTMLTextAreaElement, TextareaProps>(
  ({ className, rows = 3, ...props }, ref) => (
    <textarea
      ref={ref}
      rows={rows}
      className={cn(
        'flex w-full rounded-sm border border-input bg-surface px-3 py-2 text-[13px] text-ink shadow-1 transition-colors duration-short',
        // ⚠️ ТОЛЬКО ПО ВЫСОТЕ. Браузерное умолчание — `resize: both`, и угол
        // растаскивается ШИРЕ модалки: лист получает горизонтальную прокрутку,
        // которой у него быть не должно. По высоте тянуть полезно и безопасно.
        'resize-y',
        // 44px по меньшей стороне на грубом указателе — WCAG 2.5.5. При
        // умолчании `rows=3` норма и так пройдена, но `rows={1}` даёт ≈37px, и
        // граница обязана держаться примитивом, а не выбором потребителя.
        '[@media(pointer:coarse)]:min-h-11',
        'placeholder:text-muted',
        'focus-visible:outline-none focus-visible:border-accent focus-visible:ring-[3px] focus-visible:ring-accent-wash',
        'disabled:cursor-not-allowed disabled:opacity-50',
        className,
      )}
      {...props}
    />
  ),
);
Textarea.displayName = 'Textarea';

export { Input, Textarea };
